import React from "react";
import { Modal, Button, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { ChatCompletionRequest } from "../../model_gen/chat";
import RequestOverview from "../Request/RequestOverview";
import { TestSetService } from "../../services/TestSetService";

interface TestRequestEditProps {
  request: ChatCompletionRequest;
  onSave: (request: ChatCompletionRequest) => void;
  modelOptions?: string[];
  onSelect?: () => void;
  isEligible?: boolean;
  testSetId?: string;
  testSetRequestId?: string;
  apiKey: string;
}

const TestRequestEdit: React.FC<TestRequestEditProps> = ({
  request,
  onSave,
  modelOptions = [],
  onSelect,
  isEligible = false,
  testSetId,
  testSetRequestId,
  apiKey,
}) => {
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [editedContent, setEditedContent] = React.useState<string | null>(null);
  const [initialContent, setInitialContent] = React.useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = React.useState<ChatCompletionRequest | null>(null);
  const [editingMessageIndex, setEditingMessageIndex] = React.useState<number | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const messages = request.params.messages || [];
  const lastMessage = messages[messages.length - 1];
  const hasAssistantMessage = lastMessage?.role === "assistant";

  const handleModalOpen = () => {
    // Initialize with either existing edited content or original content
    const testResponse = request.metadata?._maitai_test_request_response;
    const content = testResponse !== undefined && testResponse !== null ? testResponse as string : lastMessage.content as string;
    setEditedContent(content);
    setInitialContent(content);
    setPendingRequest(request);
    setIsModalVisible(true);
  };

  const handleMessageEdit = (index: number, content: string) => {
    setEditedContent(content);
    setPendingRequest(request);
  };

  const handleModelChange = (model: string) => {
    const newRequest = {
      ...request,
      params: {
        ...request.params,
        model,
      },
    };
    setPendingRequest(newRequest);
  };

  const handleSave = () => {
    if (!pendingRequest || !editedContent) return;
    
    if (!testSetRequestId || isEligible) {
      // This request hasn't been added to the test set yet
      pendingRequest.metadata._maitai_test_request_response = editedContent;
      onSave(pendingRequest);
      setEditedContent(null);
      setInitialContent(null);
      setPendingRequest(null);
      setIsModalVisible(false);
      
      // If this is an eligible request and we have an onSelect handler, call it after saving
      if (isEligible && onSelect) {
        onSelect();
      }
      return;
    }

    setIsLoading(true);
    TestSetService.updateTestSetRequestResponse(
      testSetId,
      pendingRequest.request_id,
      {
        test_set_request_id: testSetRequestId,
        message_content: editedContent
      },
      setIsLoading,
      apiKey
    ).subscribe(
      () => {
        pendingRequest.metadata._maitai_test_request_response = editedContent;
        onSave(pendingRequest);
        setEditedContent(null);
        setInitialContent(null);
        setPendingRequest(null);
        setIsModalVisible(false);
        message.success("Message updated successfully");
      },
      (error: any) => {
        console.error("Failed to update message:", error);
        message.error("Failed to update message");
      }
    );
  };

  const handleCancel = () => {
    setEditedContent(null);
    setInitialContent(null);
    setPendingRequest(null);
    setEditingMessageIndex(null);
    setIsModalVisible(false);
  };

  const hasChanges = editedContent !== initialContent;

  return (
    <>
      <Button
        type="link"
        icon={<EditOutlined />}
        onClick={handleModalOpen}
        size="small"
        disabled={!hasAssistantMessage}
        title={!hasAssistantMessage ? "No assistant message to edit" : "Edit request"}
      />

      <Modal
        title="Edit Request"
        open={isModalVisible}
        onCancel={handleCancel}
        width={1000}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={isLoading}
            disabled={!pendingRequest || !editedContent || !hasChanges}>
            Save
          </Button>,
        ]}>
        <RequestOverview
          request={{
            ...pendingRequest || request,
            params: {
              ...(pendingRequest || request).params,
              messages: messages.map((msg, i) => 
                i === messages.length - 1 ? { ...msg, content: editedContent || msg.content } : msg
              )
            }
          }}
          isEditable={true}
          onMessageEdit={handleMessageEdit}
          onModelChange={handleModelChange}
          modelOptions={modelOptions}
          editingMessageIndex={editingMessageIndex}
          onEditingMessageChange={setEditingMessageIndex}
          editMode="final-assistant"
          showDetails={false}
          expanded={true}
        />
      </Modal>
    </>
  );
};

export default TestRequestEdit;
